import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Phone, Mail, Building2, Truck, Handshake, X, ArrowDown, ArrowUp, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';

import { AppTopBar } from '../components/AppTopBar';
import { AppSidebar } from '../components/AppSidebar';
import { AppSelectDropdown } from '../components/AppSelectDropdown';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { CreateOrganizationModal } from '../features/contacts/CreateOrganizationModal';
import { CreateContactModal } from '../features/contacts/CreateContactModal';
import { LeadRowActionsMenu } from '../features/leads/components/LeadRowActionsMenu';
import { SETTINGS_SELECT_TRIGGER_CLASS } from '../features/settings/utils/settingsSectionStyles';
import {
  Contact,
  ContactKind,
  CONTACT_KINDS,
  Organization,
  OrganizationType,
  ORGANIZATION_TYPES,
  contactDisplayName,
} from '../types';
import {
  contactDetailPath,
  contactKindFromSlug,
  contactKindToSlug,
  organizationDetailPath,
  organizationTypeFromSlug,
  organizationTypeToSlug,
} from '../utils/contactRoutes';
import { PageLoadingState } from '../components/LoadingSpinner';

/** Tabellenkopf-Zelle (nicht sortierbar). */
const TABLE_TH_CLASS =
  'sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-4 py-2.5 text-left text-xs font-semibold text-app-text-subtle';

type SortDir = 'asc' | 'desc';

type OrgSortKey =
  | 'name'
  | 'type'
  | 'primaryContact'
  | 'customerNumber'
  | 'address'
  | 'created';

type ContactSortKey =
  | 'name'
  | 'kind'
  | 'company'
  | 'email'
  | 'phone'
  | 'customerNumber'
  | 'created';

function SortableHeaderButton({
  label,
  active,
  dir,
  onSort,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onSort: () => void;
}) {
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-4 py-2.5"
    >
      <button
        type="button"
        onClick={onSort}
        className={`group/sort inline-flex w-full items-center gap-1 text-xs font-semibold transition-colors ${
          active ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'
        }`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

/** Segment-Track wie Plantafel: `h-9`, ohne Extra-Border (sonst wirkt er niedriger als die Suche). */
const FILTER_SEGMENT_WRAP =
  'hidden xl:inline-flex h-9 min-w-0 items-center gap-0.5 rounded-md bg-app-surface-muted p-0.5';

const FILTER_SEGMENT_BTN =
  'inline-flex h-8 items-center rounded-md px-2.5 text-[13px] font-medium leading-none transition-colors';

const SEARCH_INPUT_CLASS =
  'box-border h-9 w-full rounded-md border border-app-border bg-app-bg pl-8 pr-8 text-[13px] leading-none text-app-text outline-none placeholder:text-slate-400 focus:border-pipedrive-blue focus:bg-app-surface focus:ring-2 focus:ring-pipedrive-blue/20 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none';

const CREATE_BTN_CLASS =
  'hidden md:inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]';

/** ~3–4 screens of h-14 rows; same default as the invoices list API. */
const PAGE_SIZE = 50;

type PaginatedList<T> = {
  items: T[];
  total: number;
  page: number;
  per_page: number;
};

function parsePaginatedList<T>(data: unknown): PaginatedList<T> {
  if (data && typeof data === 'object' && Array.isArray((data as PaginatedList<T>).items)) {
    const payload = data as PaginatedList<T>;
    return {
      items: payload.items,
      total: Number(payload.total ?? payload.items.length) || 0,
      page: Number(payload.page ?? 1) || 1,
      per_page: Number(payload.per_page ?? payload.items.length) || payload.items.length,
    };
  }
  const items = Array.isArray(data) ? (data as T[]) : [];
  return { items, total: items.length, page: 1, per_page: items.length };
}

function initialsFromContact(c: Contact): string {
  const fn = (c.first_name ?? '').trim();
  const ln = (c.last_name ?? '').trim();
  if (fn && ln) return `${fn[0]}${ln[0]}`.toUpperCase();
  const dn = contactDisplayName(c).trim();
  if (!dn) return '?';
  const parts = dn.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return dn.slice(0, 2).toUpperCase();
}

type Tab = 'organizations' | 'contacts';

const tabFromParams = (raw: string | null): Tab =>
  raw === 'contacts' ? 'contacts' : 'organizations';

function listTotalLabel(count: number, singular: string, plural: string, loading: boolean): string {
  if (loading) return '…';
  return `${count} ${count === 1 ? singular : plural}`;
}

function ListPaginationBar({
  total,
  page,
  pageSize,
  loading,
  onPageChange,
}: {
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  const pageCount = total === 0 ? 0 : Math.ceil(total / pageSize);
  if (pageCount <= 1) return null;
  const rangeFrom = (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-app-border bg-app-surface px-4 py-2.5 text-xs text-app-text-subtle">
      <span className="tabular-nums">
        {`${rangeFrom.toLocaleString('de-DE')}–${rangeTo.toLocaleString('de-DE')} von ${total.toLocaleString('de-DE')}`}
        {` · Seite ${page} / ${pageCount}`}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1 || loading}
          onClick={() => onPageChange(page - 1)}
          className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 font-medium text-app-text-muted hover:bg-app-hover disabled:pointer-events-none disabled:opacity-40"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          Zurück
        </button>
        <button
          type="button"
          disabled={page >= pageCount || loading}
          onClick={() => onPageChange(page + 1)}
          className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 font-medium text-app-text-muted hover:bg-app-hover disabled:pointer-events-none disabled:opacity-40"
        >
          Weiter
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
}

async function parseApiError(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  } catch {
    /* ignore */
  }
  return fallback;
}

export const ContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabFromParams(params.get('tab'));
  const orgFilter: '' | OrganizationType =
    tab === 'organizations' ? organizationTypeFromSlug(params.get('type')) : '';
  const contactFilter: '' | ContactKind =
    tab === 'contacts' ? contactKindFromSlug(params.get('kind')) : '';

  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [orgs, setOrgs] = React.useState<Organization[]>([]);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [orgSort, setOrgSort] = React.useState<{ key: OrgSortKey; dir: SortDir }>({
    key: 'name',
    dir: 'asc',
  });
  const [contactSort, setContactSort] = React.useState<{ key: ContactSortKey; dir: SortDir }>({
    key: 'name',
    dir: 'asc',
  });
  const [loading, setLoading] = React.useState(true);
  const [orgModalOpen, setOrgModalOpen] = React.useState(false);
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [deleteOrgTarget, setDeleteOrgTarget] = React.useState<Organization | null>(null);
  const [deleteContactTarget, setDeleteContactTarget] = React.useState<Contact | null>(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const loadRequestRef = React.useRef(0);

  // Debounce search for API calls.
  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    setPage(1);
    setTotal(0);
  }, [tab, orgFilter, contactFilter, debouncedSearch]);

  const switchTab = (next: Tab) => {
    const newParams = new URLSearchParams(params);
    if (next === 'contacts') {
      newParams.set('tab', 'contacts');
      newParams.delete('type');
    } else {
      newParams.delete('tab');
      newParams.delete('kind');
    }
    setParams(newParams, { replace: true });
  };

  const setOrgFilter = (value: '' | OrganizationType) => {
    const newParams = new URLSearchParams(params);
    if (value) newParams.set('type', organizationTypeToSlug(value));
    else newParams.delete('type');
    setParams(newParams, { replace: true });
  };

  const setContactFilter = (value: '' | ContactKind) => {
    const newParams = new URLSearchParams(params);
    if (value) newParams.set('kind', contactKindToSlug(value));
    else newParams.delete('kind');
    setParams(newParams, { replace: true });
  };

  const handleOrgSort = (key: OrgSortKey) => {
    setPage(1);
    setOrgSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const handleContactSort = (key: ContactSortKey) => {
    setPage(1);
    setContactSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const loadOrgs = React.useCallback(async () => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    const qs = new URLSearchParams({
      page: String(page),
      per_page: String(PAGE_SIZE),
      sort: orgSort.key,
      dir: orgSort.dir,
    });
    if (debouncedSearch) qs.set('q', debouncedSearch);
    if (orgFilter) qs.set('type', orgFilter);
    try {
      const res = await fetch(`/api/organizations?${qs.toString()}`);
      const data = res.ok ? await res.json() : null;
      if (requestId !== loadRequestRef.current) return;
      const parsed = parsePaginatedList<Organization>(data);
      setOrgs(parsed.items);
      setTotal(parsed.total);
    } catch {
      if (requestId !== loadRequestRef.current) return;
      setOrgs([]);
      setTotal(0);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }, [debouncedSearch, orgFilter, page, orgSort.key, orgSort.dir]);

  const loadContacts = React.useCallback(async () => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    const qs = new URLSearchParams({
      page: String(page),
      per_page: String(PAGE_SIZE),
      sort: contactSort.key,
      dir: contactSort.dir,
    });
    if (debouncedSearch) qs.set('q', debouncedSearch);
    if (contactFilter) qs.set('kind', contactFilter);
    try {
      const res = await fetch(`/api/contacts?${qs.toString()}`);
      const data = res.ok ? await res.json() : null;
      if (requestId !== loadRequestRef.current) return;
      const parsed = parsePaginatedList<Contact>(data);
      setContacts(parsed.items);
      setTotal(parsed.total);
    } catch {
      if (requestId !== loadRequestRef.current) return;
      setContacts([]);
      setTotal(0);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }, [debouncedSearch, contactFilter, page, contactSort.key, contactSort.dir]);

  React.useEffect(() => {
    if (tab === 'organizations') void loadOrgs();
    else void loadContacts();
  }, [tab, loadOrgs, loadContacts]);

  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
    if (total > 0 && page > maxPage) setPage(maxPage);
  }, [total, page]);

  const handleOrgCreated = (org: Organization) => {
    setOrgModalOpen(false);
    navigate(organizationDetailPath(org.id));
  };

  const handleContactCreated = (contact: Contact) => {
    setContactModalOpen(false);
    navigate(contactDetailPath(contact.id));
  };

  const executeDeleteOrg = async () => {
    if (!deleteOrgTarget) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/organizations/${encodeURIComponent(deleteOrgTarget.id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error(await parseApiError(res, 'Organisation konnte nicht gelöscht werden.'));
      }
      setDeleteOrgTarget(null);
      const nextTotal = Math.max(0, total - 1);
      const maxPage = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE) || 1);
      if (page > maxPage) setPage(maxPage);
      else void loadOrgs();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Organisation konnte nicht gelöscht werden.');
      setDeleteOrgTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const executeDeleteContact = async () => {
    if (!deleteContactTarget) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/contacts/${encodeURIComponent(deleteContactTarget.id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error(await parseApiError(res, 'Kontakt konnte nicht gelöscht werden.'));
      }
      setDeleteContactTarget(null);
      const nextTotal = Math.max(0, total - 1);
      const maxPage = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE) || 1);
      if (page > maxPage) setPage(maxPage);
      else void loadContacts();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Kontakt konnte nicht gelöscht werden.');
      setDeleteContactTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-app-bg font-sans text-app-text antialiased">
      <AppSidebar active="contacts" />

      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Kontakte" />

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex h-full min-h-0 flex-col overflow-hidden bg-app-surface">
            <div className="flex items-stretch justify-between gap-3 border-b border-app-border bg-app-surface px-4">
              <div className="flex min-w-0 items-stretch gap-3">
                <div className="flex min-w-0" role="tablist" aria-label="Bereich">
                  {[
                    { id: 'organizations' as Tab, label: 'Organisationen' },
                    { id: 'contacts' as Tab, label: 'Kontakte' },
                  ].map((t, index) => {
                    const selected = tab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        id={`contacts-hub-tab-${t.id}`}
                        aria-controls={`contacts-hub-panel-${t.id}`}
                        onClick={() => switchTab(t.id)}
                        className={`relative shrink-0 border-b-2 py-[11px] text-sm font-medium transition-colors -mb-px ${
                          index === 0 ? 'pr-3.5 md:pr-4' : 'px-3.5 md:px-4'
                        } ${
                          selected
                            ? 'border-pipedrive-blue text-pipedrive-blue'
                            : 'border-transparent text-app-text-subtle hover:text-app-text'
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
                <span
                  className="hidden shrink-0 self-center text-sm tabular-nums text-app-text-subtle xl:inline"
                  aria-live="polite"
                >
                  {tab === 'organizations'
                    ? listTotalLabel(total, 'Organisation', 'Organisationen', loading)
                    : listTotalLabel(total, 'Kontakt', 'Kontakte', loading)}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-2.5 py-1.5">
                {tab === 'organizations' ? (
                  <>
                    <AppSelectDropdown
                      value={orgFilter}
                      onChange={(v) => setOrgFilter((v as OrganizationType | '') || '')}
                      options={[
                        { value: '', label: 'Alle' },
                        ...ORGANIZATION_TYPES.map((t) => ({ value: t, label: t })),
                      ]}
                      className="w-[10.5rem] shrink-0 xl:hidden"
                      triggerClassName={`${SETTINGS_SELECT_TRIGGER_CLASS} !rounded-md border-app-border bg-app-bg text-[13px]`}
                    />
                    <div className={FILTER_SEGMENT_WRAP} role="group" aria-label="Organisationstyp filtern">
                      {[
                        { value: '' as OrganizationType | '', label: 'Alle' },
                        ...ORGANIZATION_TYPES.map((t) => ({ value: t, label: t })),
                      ].map(({ value, label }) => (
                        <button
                          key={value || 'alle'}
                          type="button"
                          aria-pressed={orgFilter === value}
                          onClick={() => setOrgFilter(value)}
                          className={`${FILTER_SEGMENT_BTN} ${
                            orgFilter === value
                              ? 'bg-app-surface text-app-text shadow-sm'
                              : 'text-app-text-subtle hover:text-app-text'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <AppSelectDropdown
                      value={contactFilter}
                      onChange={(v) => setContactFilter((v as ContactKind | '') || '')}
                      options={[
                        { value: '', label: 'Alle' },
                        ...CONTACT_KINDS.map((t) => ({ value: t, label: t })),
                      ]}
                      className="w-[10.5rem] shrink-0 xl:hidden"
                      triggerClassName={`${SETTINGS_SELECT_TRIGGER_CLASS} !rounded-md border-app-border bg-app-bg text-[13px]`}
                    />
                    <div className={FILTER_SEGMENT_WRAP} role="group" aria-label="Kontaktart filtern">
                      {[
                        { value: '' as ContactKind | '', label: 'Alle' },
                        ...CONTACT_KINDS.map((t) => ({ value: t, label: t })),
                      ].map(({ value, label }) => (
                        <button
                          key={value || 'alle'}
                          type="button"
                          aria-pressed={contactFilter === value}
                          onClick={() => setContactFilter(value)}
                          className={`${FILTER_SEGMENT_BTN} ${
                            contactFilter === value
                              ? 'bg-app-surface text-app-text shadow-sm'
                              : 'text-app-text-subtle hover:text-app-text'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="relative w-40 shrink-0 sm:w-44 md:w-48 lg:w-52">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      tab === 'organizations' ? 'Organisation suchen …' : 'Kontakt suchen …'
                    }
                    className={SEARCH_INPUT_CLASS}
                    aria-label={tab === 'organizations' ? 'Organisationen durchsuchen' : 'Kontakte durchsuchen'}
                  />
                  {search ? (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-app-text-muted"
                      aria-label="Suche löschen"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>

                {tab === 'organizations' ? (
                  <button type="button" onClick={() => setOrgModalOpen(true)} className={CREATE_BTN_CLASS}>
                    <Plus className="h-4 w-4" /> Organisation
                  </button>
                ) : (
                  <button type="button" onClick={() => setContactModalOpen(true)} className={CREATE_BTN_CLASS}>
                    <Plus className="h-4 w-4" /> Kontakt
                  </button>
                )}
              </div>
            </div>

            {/* List body */}
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              role="tabpanel"
              id={`contacts-hub-panel-${tab}`}
              aria-labelledby={`contacts-hub-tab-${tab}`}
            >
              {deleteError && (
                <div
                  className="mx-4 md:mx-6 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
                  role="alert"
                >
                  {deleteError}
                </div>
              )}
              {tab === 'organizations' ? (
                <OrganizationsList
                  key={`orgs-${page}`}
                  loading={loading}
                  orgs={orgs}
                  sort={orgSort}
                  onSort={handleOrgSort}
                  deletingId={deleteLoading ? deleteOrgTarget?.id ?? null : null}
                  onOpen={(org) => navigate(organizationDetailPath(org.id))}
                  onRequestDelete={(org) => {
                    setDeleteError(null);
                    setDeleteOrgTarget(org);
                  }}
                />
              ) : (
                <ContactsList
                  key={`contacts-${page}`}
                  loading={loading}
                  contacts={contacts}
                  sort={contactSort}
                  onSort={handleContactSort}
                  deletingId={
                    deleteLoading && deleteContactTarget?.id != null
                      ? String(deleteContactTarget.id)
                      : null
                  }
                  onOpen={(contact) => navigate(contactDetailPath(contact.id))}
                  onRequestDelete={(contact) => {
                    setDeleteError(null);
                    setDeleteContactTarget(contact);
                  }}
                />
              )}
              <ListPaginationBar
                total={total}
                page={page}
                pageSize={PAGE_SIZE}
                loading={loading}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>

        {/* Mobile FAB */}
      <button
        type="button"
        onClick={() => (tab === 'organizations' ? setOrgModalOpen(true) : setContactModalOpen(true))}
        className="fixed bottom-20 right-5 z-30 md:hidden h-14 w-14 rounded-full bg-emerald-500 text-white shadow-lg flex items-center justify-center hover:bg-emerald-600"
        aria-label={tab === 'organizations' ? 'Neue Organisation' : 'Neuen Kontakt anlegen'}
      >
        <Plus className="w-6 h-6" />
      </button>
      </main>

      {orgModalOpen && (
        <CreateOrganizationModal
          variant="drawer"
          onClose={() => setOrgModalOpen(false)}
          onCreated={handleOrgCreated}
        />
      )}
      {contactModalOpen && (
        <CreateContactModal
          variant="drawer"
          onClose={() => setContactModalOpen(false)}
          onCreated={handleContactCreated}
        />
      )}

      <AppConfirmDialog
        open={deleteOrgTarget != null}
        onClose={() => !deleteLoading && setDeleteOrgTarget(null)}
        title="Organisation löschen?"
        description={
          deleteOrgTarget ? (
            <>
              Soll die Organisation{' '}
              <span className="font-semibold text-app-text">„{deleteOrgTarget.name}“</span> wirklich
              gelöscht werden? Sie erscheint danach nicht mehr in der Übersicht.
            </>
          ) : null
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={deleteLoading}
        onConfirm={executeDeleteOrg}
      />

      <AppConfirmDialog
        open={deleteContactTarget != null}
        onClose={() => !deleteLoading && setDeleteContactTarget(null)}
        title="Kontakt löschen?"
        description={
          deleteContactTarget ? (
            <>
              Soll der Kontakt{' '}
              <span className="font-semibold text-app-text">
                „{contactDisplayName(deleteContactTarget) || 'Unbenannt'}“
              </span>{' '}
              wirklich gelöscht werden? Er erscheint danach nicht mehr in der Übersicht.
              Bestehende Projekte und Ausschreibungen behalten den Kundennamen.
            </>
          ) : null
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={deleteLoading}
        onConfirm={executeDeleteContact}
      />
    </div>
  );
};

/* ----------------------------- List components ---------------------------- */

function contactAvatarStyle(kind: ContactKind | null | undefined): { bg: string; text: string } {
  switch (kind) {
    case 'Privatkunde':        return { bg: 'bg-emerald-100', text: 'text-emerald-800' };
    case 'Gewerblicher Kontakt': return { bg: 'bg-blue-100',  text: 'text-blue-800'    };
    default:                   return { bg: 'bg-app-surface-muted',   text: 'text-app-text-muted'    };
  }
}

function orgIconStyle(type: OrganizationType | null): { bg: string; text: string; Icon: React.ElementType } {
  // Gleiche Pastell-Intensität wie Kontakt-Avatare (bg-*-100 / text-*-700), ohne knalliges Amber.
  switch (type) {
    case 'Gewerblicher Kunde':
      return { bg: 'bg-blue-100', text: 'text-blue-700', Icon: Building2 };
    case 'Lieferant':
      return { bg: 'bg-app-surface-muted', text: 'text-app-text-muted', Icon: Truck };
    case 'Partner':
      return { bg: 'bg-emerald-100', text: 'text-emerald-700', Icon: Handshake };
    default:
      return { bg: 'bg-app-surface-muted', text: 'text-app-text-subtle', Icon: Building2 };
  }
}

function organizationListAddress(o: Organization): string {
  const reg = (o.registered_address ?? '').trim();
  if (reg) return reg;
  return (o.billing_contact?.address ?? '').trim() || '—';
}

function organizationPrimaryContactLabel(o: Organization): string {
  const name = contactDisplayName(o.primary_contact ?? undefined).trim();
  return name || '—';
}

const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && Boolean(target.closest('a, button, input, [role="menu"]'));

type OrganizationsListProps = {
  loading: boolean;
  orgs: Organization[];
  sort: { key: OrgSortKey; dir: SortDir };
  onSort: (key: OrgSortKey) => void;
  deletingId: string | null;
  onOpen: (org: Organization) => void;
  onRequestDelete: (org: Organization) => void;
};

const OrganizationsList: React.FC<OrganizationsListProps> = ({
  loading,
  orgs,
  sort,
  onSort,
  deletingId,
  onOpen,
  onRequestDelete,
}) => {
  const [menuOpenId, setMenuOpenId] = React.useState<string | null>(null);

  if (loading) {
    return <PageLoadingState />;
  }
  if (orgs.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-app-text-subtle">Noch keine Organisationen.</div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="app-scrollbar min-h-0 flex-1 divide-y divide-app-border/60 overflow-y-auto overscroll-contain bg-app-surface md:hidden">
        {orgs.map((o) => {
          const href = organizationDetailPath(o.id);
          const isDeleting = deletingId === o.id;
          const actionsPinned = menuOpenId === o.id;
          return (
            <div key={o.id} className={`group flex items-center gap-1 pr-2 ${isDeleting ? 'opacity-50' : ''}`}>
              <button
                type="button"
                onClick={() => onOpen(o)}
                className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 text-left hover:bg-app-hover active:bg-app-surface-muted/80"
              >
                {(() => {
                  const s = orgIconStyle(o.organization_type);
                  return (
                    <span
                      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.08)] ${s.bg} ${s.text}`}
                    >
                      <s.Icon className="h-5 w-5" />
                    </span>
                  );
                })()}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-app-text">{o.name}</span>
                  <span className="block truncate text-xs tabular-nums text-app-text-subtle">
                    {[o.organization_type, o.customer_number].filter(Boolean).join(' · ') || '—'}
                  </span>
                </span>
              </button>
              <LeadRowActionsMenu
                detailHref={href}
                onOpen={() => onOpen(o)}
                onDelete={() => onRequestDelete(o)}
                disabled={isDeleting}
                onOpenChange={(open) => setMenuOpenId(open ? o.id : null)}
                className={`transition-opacity focus-visible:opacity-100 ${actionsPinned ? 'opacity-100' : 'opacity-100'}`}
              />
            </div>
          );
        })}
      </div>

      <div className="app-scrollbar hidden min-h-0 flex-1 overflow-auto overscroll-contain md:flex md:flex-col">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <SortableHeaderButton
                label="Name"
                active={sort.key === 'name'}
                dir={sort.dir}
                onSort={() => onSort('name')}
              />
              <SortableHeaderButton
                label="Typ"
                active={sort.key === 'type'}
                dir={sort.dir}
                onSort={() => onSort('type')}
              />
              <SortableHeaderButton
                label="Hauptkontakt"
                active={sort.key === 'primaryContact'}
                dir={sort.dir}
                onSort={() => onSort('primaryContact')}
              />
              <SortableHeaderButton
                label="Kundennummer"
                active={sort.key === 'customerNumber'}
                dir={sort.dir}
                onSort={() => onSort('customerNumber')}
              />
              <SortableHeaderButton
                label="Firmensitz"
                active={sort.key === 'address'}
                dir={sort.dir}
                onSort={() => onSort('address')}
              />
              <SortableHeaderButton
                label="Erstellt"
                active={sort.key === 'created'}
                dir={sort.dir}
                onSort={() => onSort('created')}
              />
              <th className={`${TABLE_TH_CLASS} w-12 px-1 text-right`}>
                <span className="sr-only">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => {
              const href = organizationDetailPath(o.id);
              const isDeleting = deletingId === o.id;
              const actionsPinned = menuOpenId === o.id;
              return (
                <tr
                  key={o.id}
                  className={`group h-14 cursor-pointer transition-colors hover:bg-app-hover ${
                    isDeleting ? 'opacity-50' : ''
                  }`}
                  onClick={(e) => {
                    if (isInteractiveTarget(e.target)) return;
                    onOpen(o);
                  }}
                >
                  <td className="border-b border-app-border/60 px-4 text-sm font-medium text-app-text">
                    <span className="inline-flex items-center gap-2.5">
                      {(() => {
                        const s = orgIconStyle(o.organization_type);
                        return (
                          <span
                            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.08)] ${s.bg} ${s.text}`}
                          >
                            <s.Icon className="h-4 w-4" />
                          </span>
                        );
                      })()}
                      {o.name}
                    </span>
                  </td>
                  <td className="border-b border-app-border/60 px-4 text-sm text-app-text-muted">
                    {o.organization_type ?? '—'}
                  </td>
                  <td
                    className="max-w-[200px] truncate border-b border-app-border/60 px-4 text-sm text-app-text-muted"
                    title={organizationPrimaryContactLabel(o)}
                  >
                    {organizationPrimaryContactLabel(o)}
                  </td>
                  <td className="border-b border-app-border/60 px-4 font-mono text-xs tabular-nums text-app-text-muted">
                    {o.customer_number ?? '—'}
                  </td>
                  <td
                    className="max-w-[260px] truncate border-b border-app-border/60 px-4 text-xs text-app-text-muted"
                    title={organizationListAddress(o)}
                  >
                    {organizationListAddress(o)}
                  </td>
                  <td className="border-b border-app-border/60 px-4 text-xs tabular-nums text-app-text-subtle">
                    {o.created_at ? new Date(o.created_at).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td className="border-b border-app-border/60 px-1 text-right">
                    <LeadRowActionsMenu
                      detailHref={href}
                      onOpen={() => onOpen(o)}
                      onDelete={() => onRequestDelete(o)}
                      disabled={isDeleting}
                      onOpenChange={(open) => setMenuOpenId(open ? o.id : null)}
                      className={`transition-opacity focus-visible:opacity-100 group-hover:opacity-100 ${
                        actionsPinned ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

type ContactsListProps = {
  loading: boolean;
  contacts: Contact[];
  sort: { key: ContactSortKey; dir: SortDir };
  onSort: (key: ContactSortKey) => void;
  deletingId: string | null;
  onOpen: (contact: Contact) => void;
  onRequestDelete: (contact: Contact) => void;
};

const ContactsList: React.FC<ContactsListProps> = ({
  loading,
  contacts,
  sort,
  onSort,
  deletingId,
  onOpen,
  onRequestDelete,
}) => {
  const [menuOpenId, setMenuOpenId] = React.useState<string | null>(null);

  if (loading) {
    return <PageLoadingState />;
  }
  if (contacts.length === 0) {
    return <div className="px-6 py-12 text-center text-sm text-app-text-subtle">Noch keine Kontakte.</div>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="app-scrollbar min-h-0 flex-1 divide-y divide-app-border/60 overflow-y-auto overscroll-contain bg-app-surface md:hidden">
        {contacts.map((c) => {
          const id = String(c.id);
          const href = contactDetailPath(c.id);
          const isDeleting = deletingId === id;
          const actionsPinned = menuOpenId === id;
          return (
            <div key={c.id} className={`group flex items-center gap-1 pr-2 ${isDeleting ? 'opacity-50' : ''}`}>
              <button
                type="button"
                onClick={() => onOpen(c)}
                className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 text-left hover:bg-app-hover active:bg-app-surface-muted/80"
              >
                {(() => {
                  const s = contactAvatarStyle(c.contact_kind);
                  return (
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold tracking-wide shadow-[0_1px_3px_rgba(0,0,0,0.08)] ${s.bg} ${s.text}`}
                    >
                      {initialsFromContact(c)}
                    </span>
                  );
                })()}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-app-text">
                    {contactDisplayName(c) || '—'}
                  </span>
                  <span className="block truncate text-xs text-app-text-subtle">
                    {(c.company ?? '').trim() || c.email || c.phone || '—'}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {c.contact_kind ? (
                    <span className="whitespace-nowrap rounded-md bg-app-surface-muted px-2 py-0.5 text-[10px] font-medium text-app-text-muted">
                      {c.contact_kind}
                    </span>
                  ) : null}
                  {c.phone ? (
                    <a
                      href={`tel:${c.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-md p-2 text-pipedrive-blue hover:bg-blue-50"
                      aria-label="Anrufen"
                    >
                      <Phone className="h-4 w-4" />
                    </a>
                  ) : null}
                </span>
              </button>
              <LeadRowActionsMenu
                detailHref={href}
                onOpen={() => onOpen(c)}
                onDelete={() => onRequestDelete(c)}
                disabled={isDeleting}
                onOpenChange={(open) => setMenuOpenId(open ? id : null)}
                className={`transition-opacity focus-visible:opacity-100 ${actionsPinned ? 'opacity-100' : 'opacity-100'}`}
              />
            </div>
          );
        })}
      </div>

      <div className="app-scrollbar hidden min-h-0 flex-1 overflow-auto overscroll-contain md:flex md:flex-col">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <SortableHeaderButton
                label="Name"
                active={sort.key === 'name'}
                dir={sort.dir}
                onSort={() => onSort('name')}
              />
              <SortableHeaderButton
                label="Kontaktart"
                active={sort.key === 'kind'}
                dir={sort.dir}
                onSort={() => onSort('kind')}
              />
              <SortableHeaderButton
                label="Unternehmen"
                active={sort.key === 'company'}
                dir={sort.dir}
                onSort={() => onSort('company')}
              />
              <SortableHeaderButton
                label="E-Mail"
                active={sort.key === 'email'}
                dir={sort.dir}
                onSort={() => onSort('email')}
              />
              <SortableHeaderButton
                label="Telefon"
                active={sort.key === 'phone'}
                dir={sort.dir}
                onSort={() => onSort('phone')}
              />
              <SortableHeaderButton
                label="Kundennr."
                active={sort.key === 'customerNumber'}
                dir={sort.dir}
                onSort={() => onSort('customerNumber')}
              />
              <SortableHeaderButton
                label="Erstellt"
                active={sort.key === 'created'}
                dir={sort.dir}
                onSort={() => onSort('created')}
              />
              <th className={`${TABLE_TH_CLASS} w-12 px-1 text-right`}>
                <span className="sr-only">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const id = String(c.id);
              const href = contactDetailPath(c.id);
              const isDeleting = deletingId === id;
              const actionsPinned = menuOpenId === id;
              return (
                <tr
                  key={c.id}
                  className={`group h-14 cursor-pointer transition-colors hover:bg-app-hover ${
                    isDeleting ? 'opacity-50' : ''
                  }`}
                  onClick={(e) => {
                    if (isInteractiveTarget(e.target)) return;
                    onOpen(c);
                  }}
                >
                  <td className="border-b border-app-border/60 px-4">
                    <span className="inline-flex items-center gap-2.5">
                      {(() => {
                        const s = contactAvatarStyle(c.contact_kind);
                        return (
                          <span
                            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.08)] ${s.bg} ${s.text}`}
                          >
                            {initialsFromContact(c)}
                          </span>
                        );
                      })()}
                      <span className="text-sm font-medium text-app-text">
                        {contactDisplayName(c) || '—'}
                      </span>
                    </span>
                  </td>
                  <td className="border-b border-app-border/60 px-4 text-sm text-app-text-muted">
                    {c.contact_kind ?? '—'}
                  </td>
                  <td
                    className="max-w-[200px] truncate border-b border-app-border/60 px-4 text-sm text-app-text-muted"
                    title={c.company ?? ''}
                  >
                    {c.company || '—'}
                  </td>
                  <td className="border-b border-app-border/60 px-4 text-sm text-app-text-muted">
                    {c.email ? (
                      <a
                        href={`mailto:${c.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-pipedrive-blue hover:underline"
                      >
                        <Mail className="h-3.5 w-3.5 shrink-0" /> {c.email}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="border-b border-app-border/60 px-4 text-sm text-app-text-muted">{c.phone || '—'}</td>
                  <td className="border-b border-app-border/60 px-4 font-mono text-xs tabular-nums text-app-text-subtle">
                    {c.customer_number ?? '—'}
                  </td>
                  <td className="border-b border-app-border/60 px-4 text-xs tabular-nums text-app-text-subtle">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td className="border-b border-app-border/60 px-1 text-right">
                    <LeadRowActionsMenu
                      detailHref={href}
                      onOpen={() => onOpen(c)}
                      onDelete={() => onRequestDelete(c)}
                      disabled={isDeleting}
                      onOpenChange={(open) => setMenuOpenId(open ? id : null)}
                      className={`transition-opacity focus-visible:opacity-100 group-hover:opacity-100 ${
                        actionsPinned ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
